/**
 * CustomerDetail — the unified customer profile screen.
 *
 * Aggregates everything we know about one customer:
 *   - Identity, contact, location
 *   - KPIs (lifetime value, total orders, outstanding balance, etc.)
 *   - All sales orders (clicking one live-fetches full detail from Dynamics)
 *   - All known phones (primary + every distinct order phone)
 *   - Matching Avaya calls (with link to /calls/:id)
 *   - NPS ratings + free-text suggestions
 */
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Mail, Phone as PhoneIcon, MapPin, ShoppingBag, Wallet,
  Calendar, Star, MessageSquare, PhoneCall, AlertCircle,
} from 'lucide-react';
import { useToast } from './ToastContainer';
import api from '../utils/api';
import './Shopify.css';

const moneyFmt = (v) => {
  if (v == null) return '—';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'SAR', maximumFractionDigits: 0,
    }).format(Number(v));
  } catch { return `SAR ${Number(v).toLocaleString()}`; }
};
// Billed order total. `total_order_amount` (F&O TotalOrderAmount) only holds a
// value while an order is OPEN — it zeroes out once invoiced (~75% of orders),
// so reading it alone shows SAR 0 almost everywhere. Fall back to the ARF
// subtotal+tax, which stays populated post-invoice. See memory
// project_dynamics_zero_revenue_wrong_column.
const orderValue = (o) =>
  Number(o.total_order_amount) || (Number(o.arf_subtotal || 0) + Number(o.arf_tax || 0));
const dateFmt = (iso) => iso ? new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const datetimeFmt = (iso) => iso ? new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

const npsBadge = (rating) => {
  if (rating >= 9) return { label: 'Promoter', bg: 'rgba(16,185,129,0.18)', color: '#10b981' };
  if (rating >= 7) return { label: 'Neutral',  bg: 'rgba(251,191,36,0.18)', color: '#fbbf24' };
  return { label: 'Detractor', bg: 'rgba(248,113,113,0.18)', color: '#f87171' };
};

const CustomerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showError } = useToast();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get(`/api/customers/${encodeURIComponent(id)}`)
      .then(r => { if (active) setData(r.data); })
      .catch(e => { if (active) showError('Failed to load customer: ' + (e.response?.data?.error || e.message)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [id, showError]);

  if (loading) return <div className="shop-loading"><div className="shop-spinner" /></div>;
  if (!data) return <div className="shop"><div className="shop-empty">Customer not found.</div></div>;

  const { customer, phones, orders, stats, calls, nps } = data;

  const daysSinceLast = stats.last_order_date
    ? Math.max(0, Math.floor((Date.now() - new Date(stats.last_order_date).getTime()) / 86400000))
    : null;

  return (
    <div className="shop">
      <button onClick={() => navigate(-1)} className="shop-action-btn" style={{ marginBottom: '1rem' }}>
        <ArrowLeft size={14} /> Back
      </button>

      <div className="shop-header">
        <div>
          <h1 className="shop-title">{customer.name_alias || customer.customer_account}</h1>
          <p className="shop-subtitle">
            {customer.customer_account}
            {customer.customer_group_id ? ` · ${customer.customer_group_id}` : ''}
            {customer.address_city ? ` · ${customer.address_city}` : ''}
            {customer._pending_master ? (
              <span style={{ marginLeft: 8, padding: '0.15rem 0.4rem', background: 'rgba(251,191,36,0.18)', color: '#fbbf24', borderRadius: 4, fontSize: '0.7rem' }}>
                master record syncing — name + contact will populate when customers backfill reaches this account
              </span>
            ) : null}
          </p>
        </div>
      </div>

      <div className="shop-kpis">
        <KpiBox icon={<Wallet size={16} />} label="Lifetime value" value={moneyFmt(stats.lifetime_value)} accent="green" />
        <KpiBox icon={<ShoppingBag size={16} />} label="Total orders" value={String(stats.total_orders || 0)} accent="blue"
                sub={stats.canceled_orders ? `${stats.canceled_orders} canceled` : null} />
        <KpiBox icon={<AlertCircle size={16} />} label="Outstanding" value={moneyFmt(stats.outstanding_balance)}
                accent={stats.outstanding_balance > 0 ? 'amber' : 'neutral'} />
        <KpiBox icon={<Calendar size={16} />} label="Last order"
                value={daysSinceLast == null ? '—' : (daysSinceLast === 0 ? 'today' : `${daysSinceLast}d ago`)}
                sub={dateFmt(stats.last_order_date)} accent="neutral" />
      </div>

      {/* Contact + phones */}
      <div className="shop-panel">
        <div className="shop-panel-head">
          <h3>Contact</h3>
          <span className="shop-panel-meta">{phones.length} known number{phones.length === 1 ? '' : 's'}</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', fontSize: '0.85rem' }}>
          <div>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.72rem', textTransform: 'uppercase' }}><Mail size={12} style={{ verticalAlign: 'middle' }} /> Email</div>
            <div style={{ marginTop: '0.25rem' }}>{customer.email || '—'}</div>
          </div>
          <div>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.72rem', textTransform: 'uppercase' }}><MapPin size={12} style={{ verticalAlign: 'middle' }} /> Address</div>
            <div style={{ marginTop: '0.25rem' }}>
              {[customer.address_district, customer.address_city, customer.address_country_iso].filter(Boolean).join(', ') || '—'}
            </div>
          </div>
        </div>
        {phones.length > 0 && (
          <div style={{ marginTop: '1rem' }}>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.72rem', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
              <PhoneIcon size={12} style={{ verticalAlign: 'middle' }} /> Phone numbers
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {phones.map(p => (
                <div key={p.phone_e164} className="shop-badge" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.3rem 0.6rem' }}>
                  <span style={{ fontWeight: 600 }}>{p.phone_e164}</span>
                  <span style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.5)' }}>
                    {p.source === 'customer_primary' ? 'primary' : p.source === 'order_arf_custphone' ? 'from order' : p.source}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Orders */}
      <div className="shop-panel">
        <div className="shop-panel-head">
          <h3>Sales orders</h3>
          <span className="shop-panel-meta">{orders.length}{orders.length >= 500 ? '+' : ''} total</span>
        </div>
        {orders.length === 0 ? (
          <div className="shop-empty">No orders yet for this customer.</div>
        ) : (
          <table className="shop-table">
            <thead>
              <tr>
                <th>Order #</th>
                <th>Status</th>
                <th>Total</th>
                <th>Balance</th>
                <th>Branch</th>
                <th>Pool</th>
                <th>Sales rep</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {orders.map(o => (
                <tr key={o.sales_order_number}>
                  <td>
                    <Link to={`/customers/orders/${encodeURIComponent(o.sales_order_number)}`} className="shop-order-link">
                      {o.sales_order_number}
                    </Link>
                    {o.sales_order_name ? (
                      <div style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.4)' }}>{o.sales_order_name}</div>
                    ) : null}
                  </td>
                  <td>
                    <span className={`shop-badge ${(o.processing_status || '').toLowerCase()}`}>
                      {o.sales_order_status || o.processing_status || '—'}
                    </span>
                  </td>
                  <td>{moneyFmt(orderValue(o))}</td>
                  <td style={{ color: o.arf_balance > 0 ? '#fbbf24' : 'rgba(255,255,255,0.4)' }}>
                    {o.arf_balance > 0 ? moneyFmt(o.arf_balance) : '—'}
                  </td>
                  <td>{o.arf_branch || '—'}</td>
                  <td>{o.sales_order_pool_id || '—'}</td>
                  <td>{o.order_taker_personnel_number || '—'}</td>
                  <td className="shop-cell-time">{dateFmt(o.order_creation_datetime)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Calls */}
      <div className="shop-panel">
        <div className="shop-panel-head">
          <h3>Avaya calls</h3>
          <span className="shop-panel-meta">{calls.length} matched</span>
        </div>
        {calls.length === 0 ? (
          <div className="shop-empty">No calls matched any of this customer's known numbers.</div>
        ) : (
          <table className="shop-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Caller</th>
                <th>Agent</th>
                <th>Direction</th>
                <th>Talk time</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {calls.map(c => (
                <tr key={c.id}>
                  <td className="shop-cell-time">{datetimeFmt(c.start_time)}</td>
                  <td>
                    <PhoneCall size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                    {c.caller_number || '—'}
                    {c.caller_name ? <div style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.5)' }}>{c.caller_name}</div> : null}
                  </td>
                  <td>{c.agent_name || c.agent_extension || '—'}</td>
                  <td>{c.direction || '—'}</td>
                  <td>{c.talking_duration_ms != null ? `${Math.round(c.talking_duration_ms / 1000)}s` : (c.duration_ms != null ? `${Math.round(c.duration_ms / 1000)}s` : '—')}</td>
                  <td>{c.answered === false ? 'Missed' : (c.status || '—')}</td>
                  <td>
                    <Link to={`/calls/${c.id}`} className="shop-action-btn" style={{ padding: '0.25rem 0.6rem', fontSize: '0.72rem' }}>
                      View call
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* NPS */}
      <div className="shop-panel">
        <div className="shop-panel-head">
          <h3>NPS ratings</h3>
          <span className="shop-panel-meta">{nps.length} response{nps.length === 1 ? '' : 's'}</span>
        </div>
        {nps.length === 0 ? (
          <div className="shop-empty">No NPS responses found for this customer.</div>
        ) : (
          <div style={{ display: 'grid', gap: '0.75rem' }}>
            {nps.map(r => {
              const b = npsBadge(r.rating);
              return (
                <div key={r.id} style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 8, padding: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <div>
                      <span className="shop-badge" style={{ background: b.bg, color: b.color, fontSize: '0.78rem', padding: '0.3rem 0.6rem' }}>
                        <Star size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                        {r.rating}/10 · {b.label}
                      </span>
                      <div style={{ marginTop: '0.4rem', fontSize: '0.78rem' }}>
                        Sales rep: <strong>{r.sales_taker_name || r.sales_taker_code}</strong>
                        {r.branch ? ` · ${r.branch}` : ''}
                        {r.order_number ? ` · order ${r.order_number}` : ''}
                      </div>
                    </div>
                    <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.5)' }}>{dateFmt(r.response_date)}</div>
                  </div>
                  {r.suggestions && (
                    <div style={{ marginTop: '0.75rem', padding: '0.6rem 0.8rem', background: 'rgba(0,0,0,0.2)', borderRadius: 6, fontSize: '0.82rem', borderLeft: '3px solid rgba(255,255,255,0.2)' }}>
                      <MessageSquare size={11} style={{ verticalAlign: 'middle', marginRight: 4, opacity: 0.5 }} />
                      "{r.suggestions}"
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const KpiBox = ({ icon, label, value, accent, sub }) => (
  <div className="shop-kpi">
    <div className="shop-kpi-head"><span className={`shop-kpi-icon ${accent}`}>{icon}</span> {label}</div>
    <div className={`shop-kpi-value ${accent}`}>{value}</div>
    {sub && <div className="shop-kpi-sub">{sub}</div>}
  </div>
);

export default CustomerDetail;
